<template>
  <!--
    P3 (student-foundation-redesign) -- one generic, student-scoped Coming
    Soon shell shared by the AI Tutor and Advisor destinations. Route
    identity/labels/copy stay per-page (passed as props from each view); this
    component only owns the shared shell so the two don't drift visually.
    No AI/advisor backend, no fake state -- purely presentational.
  -->
  <div class="student-coming-soon" :data-testid="testid">
    <router-link
      :to="`/${$i18n.locale}/dashboard`"
      class="student-coming-soon__back"
      data-testid="coming-soon-back"
    >
      <student-home-icon name="arrow" :size="16" />
      {{ $t('studentComingSoon.back') }}
    </router-link>

    <span class="student-coming-soon__icon" aria-hidden="true">
      <student-home-icon :name="icon" :size="30" />
    </span>

    <p class="student-coming-soon__eyebrow">{{ eyebrow }}</p>
    <h1 class="student-coming-soon__heading">
      {{ heading }}
      <span class="student-coming-soon__badge">{{
        $t('studentComingSoon.badge')
      }}</span>
    </h1>
    <p class="student-coming-soon__body">{{ body }}</p>

    <!--
      SX-02 (doc 22 §5.1): the CTA is supplied by the page. It used to be a
      hardcoded link into the teacher-request flow, which is Coming Soon
      itself; no page may point a Coming Soon shell at a gated flow.
    -->
    <router-link
      v-if="ctaTo"
      :to="ctaTo"
      class="student-coming-soon__cta"
      data-testid="coming-soon-cta"
    >
      {{ ctaLabel }}
      <student-home-icon name="arrow" :size="16" />
    </router-link>
  </div>
</template>

<script>
import StudentHomeIcon from './StudentHomeIcon.vue'

export default {
  name: 'StudentComingSoonScreen',
  components: { StudentHomeIcon },
  props: {
    icon: { type: String, required: true },
    eyebrow: { type: String, required: true },
    heading: { type: String, required: true },
    body: { type: String, required: true },
    testid: { type: String, default: 'student-coming-soon' },
    // Optional real destination for the CTA; without one no CTA renders.
    ctaTo: { type: [String, Object], default: null },
    ctaLabel: { type: String, default: '' },
  },
}
</script>

<style lang="scss" scoped>
$cs-ink: #071447;
$cs-indigo: #314bd6;
$cs-indigo-50: #eef2ff;
$cs-muted: #65718d;
$cs-amber-50: #fff7ed;
$cs-amber-700: #9a5b13;

.student-coming-soon {
  max-width: 560px;
  margin: 0 auto;
  padding: 40px 20px;
  @include flex-container(column, flex-start, flex-start);
}

.student-coming-soon__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $cs-muted;
  @include font-category('helper-text');
  text-decoration: none;
  margin-bottom: 24px;

  &:hover,
  &:focus-visible {
    color: $cs-indigo;
    text-decoration: none;
  }
}

// P43-02 (Owner UAT, superseding P42-03): the Back arrow is «→» in Persian and
// «←» in English. `StudentHomeIcon`'s `arrow` glyph is drawn left-pointing
// (`M19 12H5` + `m11 6-6 6 6 6`), so the RTL locale is the case that needs the
// mirror. Presentation only: the link's `:to` is untouched.
html.is-rtl .student-coming-soon__back svg {
  transform: scaleX(-1);
}

.student-coming-soon__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: $cs-indigo-50;
  color: $cs-indigo;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.student-coming-soon__eyebrow {
  @include font-category('helper-text');
  color: $cs-indigo;
  font-weight: 700;
  margin: 0 0 8px;
}

.student-coming-soon__heading {
  @include font-category('display-small');
  color: $cs-ink;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.student-coming-soon__badge {
  @include font-category('helper-text');
  font-size: 12px;
  color: $cs-amber-700;
  background: $cs-amber-50;
  border-radius: 999px;
  padding: 3px 11px;
  font-weight: 700;
}

.student-coming-soon__body {
  @include font-category('body');
  color: $cs-muted;
  margin: 0 0 28px;
}

.student-coming-soon__cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $cs-indigo;
  color: #fff;
  border-radius: 999px;
  padding: 12px 22px;
  font-weight: 700;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    background: darken($cs-indigo, 8%);
    text-decoration: none;
  }
}

// The CTA is a Forward action: «←» in Persian (as drawn), «→» in English.
html:not(.is-rtl) .student-coming-soon__cta svg {
  transform: scaleX(-1);
}
</style>
